import React, { useState } from "react";
import { toast } from "sonner";
import { User, Phone, MessageCircle, Mail, Building2, MapPin, Clock, Save } from "lucide-react";
import { CONTACT } from "@/config";

const AdminSettings: React.FC = () => {
  const [form, setForm] = useState({
    name: CONTACT.name,
    role: CONTACT.role,
    phone: CONTACT.phone,
    whatsapp: CONTACT.phoneIntl,
    email: CONTACT.email,
    dealership: CONTACT.dealership,
    dealershipPhone: CONTACT.dealershipPhone,
    address: CONTACT.address.join(", "),
    hours: CONTACT.hours.join("\n"),
  });

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const save = (e: React.FormEvent) => {
    e.preventDefault();
    toast.success("Settings saved. Connect a backend to persist these values.");
  };

  const input = "w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100";
  const label = "mb-1.5 block text-xs font-semibold text-slate-600";

  return (
    <form onSubmit={save} className="max-w-3xl space-y-6">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-400">Preferences</p>
        <h1 className="text-2xl font-extrabold text-slate-900">Site Settings</h1>
        <p className="mt-1 text-sm text-slate-500">These details power the contact buttons and information shown across your website.</p>
      </div>

      <div className="card-soft p-6">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900"><User className="h-5 w-5 text-blue-600" /> Profile</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div><label className={label}>Full Name</label><input className={input} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="settings-name" /></div>
          <div><label className={label}>Role / Title</label><input className={input} value={form.role} onChange={(e) => set("role", e.target.value)} data-testid="settings-role" /></div>
        </div>
      </div>

      <div className="card-soft p-6">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900"><Phone className="h-5 w-5 text-blue-600" /> Contact</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div><label className={label}><Phone className="mr-1 inline h-3.5 w-3.5" /> Phone (call)</label><input className={input} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="settings-phone" /></div>
          <div><label className={label}><MessageCircle className="mr-1 inline h-3.5 w-3.5" /> WhatsApp (intl)</label><input className={input} value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} data-testid="settings-whatsapp" /></div>
          <div className="sm:col-span-2"><label className={label}><Mail className="mr-1 inline h-3.5 w-3.5" /> Email</label><input className={input} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="settings-email" /></div>
        </div>
      </div>

      <div className="card-soft p-6">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900"><Building2 className="h-5 w-5 text-blue-600" /> Dealership</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div><label className={label}>Dealership Name</label><input className={input} value={form.dealership} onChange={(e) => set("dealership", e.target.value)} data-testid="settings-dealership" /></div>
          <div><label className={label}>Dealership Number</label><input className={input} value={form.dealershipPhone} onChange={(e) => set("dealershipPhone", e.target.value)} data-testid="settings-dealership-phone" /></div>
          <div className="sm:col-span-2"><label className={label}><MapPin className="mr-1 inline h-3.5 w-3.5" /> Address</label><input className={input} value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="settings-address" /></div>
          <div className="sm:col-span-2"><label className={label}><Clock className="mr-1 inline h-3.5 w-3.5" /> Business Hours</label><textarea rows={3} className={input} value={form.hours} onChange={(e) => set("hours", e.target.value)} data-testid="settings-hours" /></div>
        </div>
      </div>

      <div className="flex justify-end">
        <button type="submit" className="btn-blue" data-testid="settings-save"><Save className="h-4 w-4" /> Save Settings</button>
      </div>
    </form>
  );
};

export default AdminSettings;
